import { ref, watch } from "vue";
import { EmotionsEvent } from "@/services/typescript-fetch-client-generated";
import { ALL_COLORS } from "@/system/config";
import { mainStore } from "@/system/mainStore";
import utils from "@/system/utils";

const chartStore = {
  init: async () => {
    watch(
      () => mainStore.sessionDataIsLoading.value,
      async (current, prev) => {
        updateChartData();
      }
    );
    watch(
      () => mainStore.sessionParticipants.value,
      async (current, prev) => {
        if (current != prev) updateChartData();
      }
    );
  },

  speakingChart: ref({
    type: "pie",
    data: {
      datasets: [
        {
          data: [] as string[],
          backgroundColor: ["red", "blue", "brown"] as string[],
          label: "Speaking",
        },
      ],
      labels: [] as string[],
      hiddenSlices: [] as number[],
    },
    options: {
      title: { display: true, text: "Speaking distribution" },
      responsive: true,
    },
  }),
  engagementChart: ref({
    type: "scatter",
    data: {
      datasets: [] as any[],
      labels: [] as number[],
    },
    options: {
      responsive: true,
      title: { display: true, text: "Participants engagement" },
      scales: {
        xAxes: [
          {
            display: true,
            scaleLabel: { display: true, labelString: "Emotions score" },
            ticks: { min: -1, max: 1 },
          },
        ],
        yAxes: [
          {
            display: true,
            scaleLabel: {
              display: true,
              labelString: "Attention and speaking score",
            },
            // ticks: { min: -1, max: 1 },
          },
        ],
      },
    },
  }),
  userEmotionsChart: ref({
    type: "scatter",
    data: { labels: [], datasets: [] as any[] },
    options: {
      // labels: [] as number[],
      responsive: true,
      title: { display: true, text: "Emotions" },
      tooltips: { mode: "index", intersect: false },
      hover: { mode: "nearest", intersect: true },
      scales: {
        xAxes: [
          {
            display: true,
            scaleLabel: { display: true, labelString: "Time (minutes)" },
          },
        ],
        yAxes: [
          {
            display: true,
            scaleLabel: { display: true, labelString: "Emotions" },
            // ticks: { min: -1, max: 1 },
          },
        ],
      },
    },
  }),
  Chart: ref({
    type: "scatter",
    data: { labels: [], datasets: [] },
    options: {
      responsive: true,
      title: { display: true, text: "Emotions" },
      tooltips: { mode: "index", intersect: false },
      hover: { mode: "nearest", intersect: true },
      scales: {
        xAxes: [
          {
            display: true,
            scaleLabel: { display: true, labelString: "Time (minutes)" },
          },
        ],
        yAxes: [
          {
            display: true,
            scaleLabel: { display: true, labelString: "Emotions" },
            ticks: { min: -1, max: 1 },
          },
        ],
      },
    },
  }),
  attentionsChart: ref({
    type: "scatter",
    data: {
      labels: [],
      datasets: [] as any[],
    },
    options: {
      responsive: true,
      title: { display: true, text: "Attention score" },
      tooltips: { mode: "index", intersect: false },
      hover: { mode: "nearest", intersect: true },
      scales: {
        xAxes: [
          {
            display: true,
            scaleLabel: { display: true, labelString: "Time (minutes)" },
          },
        ],
        yAxes: [
          {
            display: true,
            scaleLabel: { display: true, labelString: "Attention" },
            // ticks: { min: -1, max: 1 },
          },
        ],
      },
    },
  }),
  emotionsChart: ref({
    type: "scatter",
    data: {
      labels: [] as any[],
      datasets: [] as any[],
    },
    options: {
      responsive: true,
      // labels: [] as number[],
      title: { display: true, text: "Score per emotion" },
      tooltips: { mode: "index", intersect: false },
      hover: { mode: "nearest", intersect: true },
      scales: {
        xAxes: [
          {
            display: true,
            scaleLabel: { display: true, labelString: "Time (minutes)" },
          },
        ],
        yAxes: [
          {
            display: true,
            scaleLabel: { display: true, labelString: "Emotion score" },
            ticks: { min: 0, max: 1 },
          },
        ],
      },
    },
  }),
};

function updateChartData() {
  let participations = mainStore.sessionParticipations.value;
  let participants = mainStore.sessionParticipants.value;

  let sum = participants.map((k) => k.speakingTime).reduce((s, t) => s + t, 0);
  let colors = [...ALL_COLORS];
  let selectedParticipants: { [key: number]: boolean } = {};
  participants.map((p) => (selectedParticipants[p.participantId] = p.show));

  // Prepare charts data
  let emotionTypes: string[] = [
    "surprised",
    "scared",
    "disgust",
    "happy",
    "sad",
    "angry",
    "neutral",
  ];

  let emotions = mainStore.sessionEmotions.value;
  let minTs = Math.min(...emotions.map((t) => t.ts));

  let groupByTs: { [key: number]: EmotionsEvent[] } = {};
  for (let emotion of emotions) {
    let ts = emotion.ts - minTs;
    if (groupByTs[ts] === undefined) groupByTs[ts] = [];
    if (selectedParticipants[emotion.participantId])
      groupByTs[ts].push(emotion);
  }

  let emotionsData = [];
  let emotionsDataByEmotion: { [key: string]: { x: number; y: number }[] } = {};
  for (let ts in groupByTs) {
    let data = groupByTs[ts];
    if (data.length === 0) continue;
    let tsData: { [key: string]: number } = {};
    emotionTypes.map((t) => (tsData[t] = 0));

    for (let emotionType of emotionTypes) {
      for (let d of data) {
        const value = (d as unknown as { [key: string]: number })[emotionType];
        if (value !== undefined) tsData[emotionType] += value;
      }

      if (data.length > 0) tsData[emotionType] /= data.length;
      if (emotionsDataByEmotion[emotionType] === undefined)
        emotionsDataByEmotion[emotionType] = [];

      emotionsDataByEmotion[emotionType].push({
        // @ts-ignore
        x: ts,
        y: tsData[emotionType],
      });
    }
  }

  for (let pos in emotionTypes) {
    let emotionType = emotionTypes[pos];
    emotionsData.push({
      label: emotionType,
      type: "scatter",
      backgroundColor: ALL_COLORS[pos],
      borderColor: ALL_COLORS[pos],
      data: utils.interpolate(emotionsDataByEmotion[emotionType]),
      spanGaps: true,
      steppedLine: false,
      fill: false,
      showLine: true,
      tension: 0,
    });
  }

  minTs = Math.min(...participations.map((t) => t.ts));
  let timestamps: { [key: number]: number } = {};
  participations.map((p) => (timestamps[p.ts] = 0));

  let datasets = [];
  let attentionsDatasets = [];
  let engagements = [];
  let allSpeakingTime = participants.reduce((s, c) => s + c.speakingTime, 0);
  for (let key in participants) {
    let p = participants[key];
    let color = colors[key];

    let participantId = p.participantId;

    let attentionSpeakingScore =
      (p.averageAttention + ((2 * p.speakingTime) / allSpeakingTime - 1)) / 2;
    engagements.push({
      label: p.name,
      type: "line",
      backgroundColor: color,
      borderColor: color,
      data: [
        { x: p.averageEmotions, y: attentionSpeakingScore.toPrecision(2) },
      ],
      fill: false,
      hidden: !p.show,
    });

    // eslint-disable-next-line no-unused-vars
    let currentSet: { x: number; y: number }[] = [];
    let attentionSets: { x: number; y: number }[] = [];

    mainStore.sessionAttentions.value
      .filter((p) => p.participantId === participantId)
      .map((p) =>
        attentionSets.push({
          x: p.ts - minTs,
          y: p.attention === 0 ? NaN : p.attention,
        })
      );
    mainStore.sessionParticipations.value
      .filter((p) => p.participantId === participantId)
      .map((p) =>
        currentSet.push({
          x: p.ts - minTs,
          y: p.participation === 0 ? NaN : p.participation,
        })
      );

    attentionSets = utils.interpolate(attentionSets);
    currentSet = utils.interpolate(currentSet);

    datasets.push({
      label: p.name,
      backgroundColor: color,
      borderColor: color,
      spanGaps: true,
      steppedLine: false,
      data: currentSet,
      fill: false,
      type: "scatter",
      showLine: true,
      tension: 0,
      hidden: !p.show,
    });

    attentionsDatasets.push({
      label: p.name,
      backgroundColor: color,
      borderColor: color,
      data: attentionSets,
      type: "scatter",
      tension: 0,
      showLine: true,
      fill: false,
      hidden: !p.show,
    });
  }

  // update charts data
  const pieData: string[] = [];
  participants.forEach((k) => {
    const result = k.show
      ? (100 * (k.speakingTime / sum)).toPrecision(4)
      : undefined;
    if (result) pieData.push(result);
  });
  chartStore.speakingChart.value = {
    type: "pie",
    data: {
      datasets: [
        {
          data: pieData,
          backgroundColor: colors.slice(0, participants.length),
          label: "Speaking",
        },
      ],
      labels: participants.map((k) => k.name),
      hiddenSlices: [1],
    },
    options: {
      ...chartStore.speakingChart.value.options,
      responsive: true,
    },
  };

  chartStore.userEmotionsChart.value = {
    type: "scatter",
    data: { ...chartStore.userEmotionsChart.value.data, datasets },
    options: {
      ...chartStore.userEmotionsChart.value.options,
      responsive: true,
      // labels: [0, 1],
    },
  };

  chartStore.emotionsChart.value = {
    type: "scatter",
    data: {
      labels: emotionTypes,
      datasets: emotionsData,
    },
    options: {
      ...chartStore.emotionsChart.value.options,
      responsive: true,
      // labels: [0, 1],
    },
  };

  chartStore.attentionsChart.value = {
    type: "scatter",
    data: {
      datasets: attentionsDatasets,
      labels: chartStore.attentionsChart.value.data.labels,
    },
    options: { ...chartStore.attentionsChart.value.options, responsive: true },
  };

  chartStore.engagementChart.value = {
    type: "line",
    data: {
      datasets: engagements,
      labels: [0, 1],
    },
    options: { ...chartStore.engagementChart.value.options, responsive: true },
  };
}

export { chartStore };
